import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

// Reusable info tooltip with optional pulsing icon.
// Uses a Portal to render tooltip at document root, avoiding overflow:hidden clipping.
// Props:
// - content: string | ReactNode
// - className: string
// - pulse: boolean (default true)
// - side: 'top' | 'bottom' | 'left' | 'right' (default 'right')
const InfoTooltip = ({ content, className = '', pulse = true, side = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);

  useEffect(() => {
    if (open && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      
      let top, left;
      switch (side) {
        case 'top':
          top = rect.top + scrollY - 8;
          left = rect.left + scrollX + rect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + scrollY + 8;
          left = rect.left + scrollX + rect.width / 2;
          break;
        case 'left':
          top = rect.top + scrollY + rect.height / 2;
          left = rect.left + scrollX - 8;
          break;
        case 'right':
        default:
          top = rect.top + scrollY + rect.height / 2;
          left = rect.right + scrollX + 8;
          break;
      }
      setPosition({ top, left });
    }
  }, [open, side]);

  const getTooltipStyle = () => {
    const base = {
      position: 'absolute',
      zIndex: 99999,
      top: position.top,
      left: position.left,
    };
    
    switch (side) {
      case 'top':
        return { ...base, transform: 'translate(-50%, -100%)' };
      case 'bottom':
        return { ...base, transform: 'translate(-50%, 0)' };
      case 'left':
        return { ...base, transform: 'translate(-100%, -50%)' };
      case 'right':
      default:
        return { ...base, transform: 'translate(0, -50%)' };
    }
  };

  return (
    <div
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Ensure pulse animation exists even if Tailwind animate-ping isn't available */}
      <style>{`
        @keyframes fb-pulse{0%{transform:scale(1);opacity:.55}70%{transform:scale(1.8);opacity:0}100%{transform:scale(1.8);opacity:0}}
      `}</style>
      <div className="relative w-4 h-4" ref={iconRef}>
        {pulse && (
          <span
            className="absolute inset-0 rounded-full"
            style={{
              animation: 'fb-pulse 1.8s ease-out infinite',
              backgroundColor: 'color-mix(in oklab, var(--succession-primary, #3b82f6) 30%, transparent)'
            }}
            aria-hidden="true"
          />
        )}
        <InformationCircleIcon
          className="w-4 h-4 relative z-10 text-succession-text-secondary"
          role="button"
          aria-label={typeof content === 'string' ? content : 'More info'}
          tabIndex={0}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        />
      </div>
      {open && typeof document !== 'undefined' && createPortal(
        <div style={getTooltipStyle()} role="tooltip">
          <div className="max-w-xs px-3 py-2 rounded-lg shadow-lg bg-succession-bg-primary border border-succession-border text-succession-text-primary text-xs whitespace-normal">
            {typeof content === 'string' ? (<span>{content}</span>) : content}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InfoTooltip;


